import * as React from 'react';

import type { ApolloError } from '@apollo/client';
import { ErrorType } from '__generated__/graphql-types';

import { isAuthenticatedUser } from 'js/lib/user';

import { NotFound as NotFoundPage } from 'bundles/common/components/withNotFound';
import EnterpriseHomeAccessErrorPage from 'bundles/program-home/components/enterprise-home/EnterpriseHomeAccessErrorPage';
import { InternalServerError, NotFoundError } from 'bundles/ssr/lib/errors/directives';
import inServerContext from 'bundles/ssr/util/inServerContext';
import { useProgramMembership } from 'bundles/unified-common/contexts/EnterpriseProgramContext';

const NotFound = () => {
  // If on server, serve a true 404 (edge will handle rendering 404 page).
  if (inServerContext) {
    throw new NotFoundError();
  } else {
    return <NotFoundPage />;
  }
};

const InternalError = ({ pathname }: { pathname: string }) => {
  // If on server, serve a true 500 (edge will handle rendering internal server error page).
  if (inServerContext) {
    throw new InternalServerError(pathname);
  } else {
    // We don't have a component for internal errors, default to showing not found.
    return <NotFoundPage />;
  }
};

// This is used when EnterpriseDescriptionPages queries complete w/ no data.
// If error is defined, we attempt to surface a correct response.
// If not, we default to a 404 page.
const EnterprisePageDataErrorHandler = ({ pathname, error }: { pathname: string; error?: ApolloError }) => {
  const unifiedProgramMembership = useProgramMembership();
  if (!error || error.graphQLErrors?.[0]?.extensions?.errorType === ErrorType.NotFound) {
    // Check if the learner is logged in. If not, show the EnterpriseHomeAccessErrorPage
    const isLoggedIn = isAuthenticatedUser();
    if (!isLoggedIn || (isLoggedIn && !unifiedProgramMembership.isProgramMember)) {
      return <EnterpriseHomeAccessErrorPage isLoggedIn={isLoggedIn} />;
    }
    // Learner is logged in and a member of the program already. This means that the page
    // truly does not exist.
    return <NotFound />;
  } else {
    return <InternalError pathname={pathname} />;
  }
};

export default EnterprisePageDataErrorHandler;
